import React, { useRef } from "react";
import { Button, Popconfirm, Skeleton } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useAdmin from "../../hooks/useAdmin";
import { AdminPageHeader } from "../../components/admin-shell/AdminShell";
import "./galary.css";

const ACCEPTED = ["image/jpeg", "image/png", "image/jpg"];

const Gallary = () => {
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const { getPhotos, uploadPhoto, deletePhoto } = useAdmin();

  const photos = useInfiniteQuery({
    queryKey: ["admin-photos"],
    queryFn: getPhotos,
    initialPageParam: 0,
    getNextPageParam: (_last, pages) => {
      const loaded = pages.reduce((sum, p) => sum + p.photos.length, 0);
      return loaded < (pages[0].count || 0) ? loaded : undefined;
    },
    refetchOnWindowFocus: false,
  });

  const list = (photos.data?.pages || []).flatMap((p) => p.photos);
  const count = photos.data?.pages?.[0]?.count || 0;

  const { mutate: upload, isPending: uploading } = useMutation({
    mutationFn: uploadPhoto,
    onSuccess: () => {
      toast.success("Photo added");
      queryClient.invalidateQueries({ queryKey: ["admin-photos"] });
    },
    onError: (err) => toast.error(err?.response?.data?.message || "Upload failed"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: deletePhoto,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-photos"] }),
    onError: () => toast.error("Could not delete the photo"),
  });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return toast.error("Use a JPG or PNG image");
    upload(file);
  };

  return (
    <>
      <AdminPageHeader
        title="Gallery"
        subtitle={photos.isLoading ? "Loading…" : `${count} photos`}
        actions={
          <Button type="primary" icon={<UploadOutlined />} loading={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? "Uploading…" : "Add photo"}
          </Button>
        }
      />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={onFile} />

      {photos.isLoading ? (
        <div className="gallery-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton.Image key={i} active className="gallery-skeleton" />
          ))}
        </div>
      ) : photos.isError ? (
        <div className="adm-card adm-empty">Could not load photos.</div>
      ) : list.length === 0 ? (
        <div className="adm-card adm-empty">No photos yet. Add the first one.</div>
      ) : (
        <div className="gallery-grid">
          {list.map((photo) => (
            <figure key={photo._id} className="gallery-item">
              <img src={photo.photo} alt="" loading="lazy" />
              <Popconfirm
                title="Delete this photo?"
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => remove(photo._id)}
              >
                <Button className="gallery-item__delete" danger icon={<DeleteOutlined />} aria-label="Delete photo" />
              </Popconfirm>
            </figure>
          ))}
        </div>
      )}

      {photos.hasNextPage ? (
        <div className="gallery-more">
          <Button size="large" loading={photos.isFetchingNextPage} onClick={() => photos.fetchNextPage()}>
            Load more
          </Button>
        </div>
      ) : null}
    </>
  );
};

export default Gallary;
